/**
 * The four kinds of tile.
 *
 * All of them render into `WidgetCard`, which already owns the loading, error
 * and empty states — so nothing here re-implements them, and a tile that fails
 * looks like every other tile that fails.
 *
 * THE DATA TILES PASS NO `onSort`. `ReportView`'s doc comment says why: a
 * placed tile must not drift from the order it was saved with. Sorting a
 * dashboard tile would change what it shows for that viewer only, silently,
 * with nothing to save it into.
 *
 * FOLDING IS DIFFERENT, and a pivot tile folds. The fold state is display
 * only — `planKey` never sees it, so the query and every figure are the same
 * either way — and it is how a reader reads a grouped report, as the builder's
 * own preview already allows a read-only reader. The saved folds are where a
 * tile starts; the reader's changes live in the tile until the page reloads.
 */

import {
  applyFold,
  definitionsFor,
  type FoldAction,
  type MetricComparison,
  type OnDrill,
  ReportFooter,
  ReportView,
} from '@clockwise/report-kit';
// THE TILE'S THEME, imported here rather than only by the workbench.
// `report-kit-theme.css` is what binds report-kit's `--rk-color-*` inputs to
// this app's shadcn tokens, and it used to be pulled in by `report-workbench`
// alone — a module no dashboard route loads. So a tile rendered with none of
// it: unmuted labels, and charts painting `var(--rk-series-1)` as solid black.
import '@/components/reports/report-kit-theme.css';
import type { DateWindow, QueryResult, ReportModel, ReportSpec } from '@clockwise/semantic-core';
import { findMeasure, grainStart } from '@clockwise/semantic-core';
import { FormulaHint } from '@clockwise/ui';
import { FileQuestion, Filter } from 'lucide-react';
import { type PointerEvent, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { WidgetCard } from '../widget-card';
import type { FilterState } from './filters';
import type { MetricComparisonSpecs } from './metric-window';
import { tileSeriesStyle } from './tile-palette';
import { useTileQuery } from './use-tile-query';

/** What the framework says under a tile about the filters that reached it. */
export interface TileNotice {
  label: string;
  state: FilterState;
}

/**
 * The FR-4.5 notice line.
 *
 * A tile that a filter could not reach is NEVER HIDDEN — holes in a dashboard
 * read as breakage, and a reader who cannot see the tile cannot tell that its
 * absence was deliberate. It says so instead, quietly, under the numbers.
 *
 * `applied` prints nothing: the normal case needs no chrome, and a line under
 * every tile saying "filtered by Customer" is noise that trains people to stop
 * reading the line that matters.
 */
function FilterNotices({ notices }: { notices: readonly TileNotice[] }) {
  const notApplicable = notices.filter((n) => n.state === 'notApplicable');
  const tileOwned = notices.filter((n) => n.state === 'tileOwned');
  if (notApplicable.length === 0 && tileOwned.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 px-1 pt-1 text-[11px] text-muted-foreground">
      {notApplicable.length > 0 && (
        <span title="This tile's data does not carry that dimension, so the filter has nothing to narrow here.">
          not filtered by {notApplicable.map((n) => n.label).join(', ')}
        </span>
      )}
      {tileOwned.length > 0 && (
        <span
          className="font-medium text-foreground"
          title="This tile carries its own filter on that dimension, so the dashboard's value does not override it."
        >
          own filter: {tileOwned.map((n) => n.label).join(', ')}
        </span>
      )}
    </div>
  );
}

interface DataTileProps {
  /**
   * A figure was clicked — the drill-down's entrance. Absent and the tile's
   * cells are plain text: while editing, and on a tile that cannot drill.
   */
  onDrill?: OnDrill;
  title: string;
  subtitle?: string;
  spec: ReportSpec | null;
  model: ReportModel | null;
  notices?: readonly TileNotice[];
  /** A KPI's prior-window value, when the tile asked for a comparison. */
  previous?: number | null;
  /** Edit-mode affordances for the header. Absent when reading. */
  controls?: ReactNode;
  /**
   * The window the reader chose, when the spec's own was widened.
   *
   * A metric tile queries further back for its history, so its spec no longer
   * says which period is being looked at. See `metricWindow`.
   */
  selectedPeriod?: { from: string; to: string };
  /**
   * A trended metric's figure and comparison, as two questions over whole
   * windows — the reader's and its mirror. See `metricComparisonSpecs`.
   */
  comparison?: MetricComparisonSpecs | null;
  /**
   * This tile's colours, first to last — see `tilePalette`. Paints a metric's
   * sparkline, and a chart whose series are its own measures, so a row of
   * tiles reads as distinct tiles rather than one green repeated.
   */
  palette?: readonly string[];
  /**
   * The tile's OWN filters, as sentences — a dashboard report's, or a linked
   * tile's additions to its report. ALWAYS MARKED, to readers too: three tiles
   * titled "Utilization" filtered to India, the US and the Philippines are
   * three different numbers, and the title is otherwise the only clue.
   */
  ownFilters?: readonly string[];
}

/**
 * A tile's own filters: a funnel in the header, the sentences on hover or tap.
 *
 * AN ICON, NOT CHIPS. The chips under the title had room for about one filter
 * on a metric tile — "Country is US, C…" — so a tile with two showed one and
 * read as if that were all, and the extra line pushed its number below its
 * unfiltered neighbours'. The funnel says "this tile is narrowed" in the row
 * the ⓘ already uses; the count says by how many; the list says by what.
 *
 * A POPOVER, NOT A TOOLTIP: a tooltip never opens on a tap, and on a tablet
 * the list would be unreachable. A mouse opens it by hovering; a tap, a click
 * or Enter opens it too, and it stays until dismissed.
 */
function OwnFilters({ filters }: { filters: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const hover = (next: boolean) => (e: PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse') setOpen(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-auto min-w-6 gap-0.5 px-1 text-muted-foreground"
          aria-label={`Filtered by: ${filters.join('; ')}`}
          onPointerEnter={hover(true)}
          onPointerLeave={hover(false)}
          // Already open (the hover opened it): a click keeps it, rather than
          // toggling shut the list the pointer just brought up.
          onClick={(e) => open && e.preventDefault()}
        >
          <Filter className="size-3" aria-hidden="true" />
          {filters.length > 1 && (
            <span className="text-[10px] tabular-nums leading-none" aria-hidden="true">
              {filters.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-auto max-w-72 px-3 py-2 text-xs"
        // Opened by a hover, it must not pull focus out of the page's flow.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ul className="space-y-0.5">
          {filters.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * A saved report, or a KPI, drawn.
 *
 * One component for both because they differ only in their spec's `viz` —
 * `ReportView` already dispatches on it, and a second component would be a
 * second place for the two to diverge.
 */
export function DataTile({
  title,
  subtitle,
  spec,
  model,
  notices = [],
  previous,
  controls,
  selectedPeriod,
  comparison,
  palette,
  ownFilters = [],
  onDrill,
}: DataTileProps) {
  const series = useTileQuery(spec, model);
  const { result, plan } = series;
  // The figure and its mirror. Skipped (a null spec) on every other tile, and
  // when both are buckets of the series already fetched.
  const ask = comparison && !comparison.fromSeries ? comparison : null;
  const current = useTileQuery(ask?.current ?? null, ask ? model : null);
  const prior = useTileQuery(ask?.previous ?? null, ask ? model : null);
  const loading = series.loading || current.loading || prior.loading;
  const error = series.error ?? current.error ?? prior.error;
  const refetch = () => {
    series.refetch();
    if (ask) {
      current.refetch();
      prior.refetch();
    }
  };
  const metricComparison = useMemo(
    () =>
      comparison?.fromSeries
        ? figureAndMirror(comparison, result, result, spec?.rows[0])
        : figureAndMirror(comparison, current.result, prior.result),
    [comparison, result, current.result, prior.result, spec],
  );
  const { shown, fold } = useTileFolds(spec);
  const metric = spec?.viz === 'metric';

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      variant={metric ? 'metric' : 'default'}
      controls={
        <>
          {ownFilters.length > 0 && <OwnFilters filters={ownFilters} />}
          {metric && spec && model && result && (
            <MetricDefinitions spec={spec} model={model} result={result} />
          )}
          {controls}
        </>
      }
      loading={loading}
      error={error ? { message: error, onRetry: refetch } : null}
      empty={isEmpty(result) ? 'No rows matched this combination.' : undefined}
    >
      {spec && model && result && !isEmpty(result) ? (
        // `rk-scope` is the library's own token carrier and `report-kit-scope`
        // is this app's binding of them; a view needs BOTH, and the tile is
        // the only place outside the builder that mounts one.
        <div
          className="rk-scope report-kit-scope flex min-h-0 flex-1 flex-col"
          // Inline, so it beats the theme's own binding on this same element.
          // NOT WITH A DIMENSION ON COLUMNS: there series 1 is a category, and
          // it must be the same colour in every tile that shows it.
          style={tileSeriesStyle(spec, palette)}
        >
          {/* A COLUMN, so the view's own children share the height rather
              than the first one pushing the rest out. `ReportView` returns a
              fragment — a truncation notice and then the grid — and this box
              never scrolls, so as a plain block the notice stole rows off the
              bottom of the card with nothing to scroll them back. As a flex
              column the notice keeps its height and the grid takes what is
              left; `.rk-table-scroll` is already a scroll container. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ReportView
              model={model}
              spec={shown ?? spec}
              result={result}
              onDrill={onDrill}
              // THE SAME PLAN THE QUERY RAN. Without it the projection takes
              // neither the memory sort nor the memory cap, so a tile ordered
              // its rows differently from the builder and — once the ceiling
              // rose past a saved spec's own `limit` — counted more of them,
              // which is one saved report showing two grand totals.
              plan={plan ?? undefined}
              previous={previous}
              comparison={metricComparison}
              selectedPeriod={selectedPeriod}
              // THE CARD ALREADY SAID IT. A metric tile is titled after its
              // measure by default, and the block below printed that same
              // label under the number — the same words twice, in a box whose
              // whole job is one number. Handing the title down lets the
              // metric drop its label when it is a copy, and keep it when the
              // author retitled the tile to something else.
              heading={title}
              // The ⓘ in the header names it, with its definition.
              measureNamed={metric}
              // And the dashboard's date chip names the window it reports.
              periodNamed
              onToggleGroup={(key) => fold({ type: 'toggleGroup', key })}
              onCollapseLevel={(dimensionId, collapsed) =>
                fold({ type: 'setLevelCollapsed', dimensionId, collapsed })
              }
            />
          </div>
          {/* The framework-owned footer: row count, truncation, ƒ definitions.
              Fed from `meta`, so a formula the server changed shows up without
              a client deploy — and a capped result cannot be silent. */}
          {/* A metric's row count is the length of its own history, which
              tells a reader nothing; its definitions moved to the header. */}
          {/* The SAVED spec: folds change none of what the footer says, and
              handing it the folded one re-ran its projection on every click. */}
          <ReportFooter
            model={model}
            spec={spec}
            result={result}
            plan={plan ?? undefined}
            summary={!metric}
          />
          <FilterNotices notices={notices} />
        </div>
      ) : null}
    </WidgetCard>
  );
}

/**
 * The numbers the metric prints, once both window queries have answered.
 * `undefined` until then — the tile is still loading — or when there is no
 * comparison to make.
 *
 * Given the series' period `dimension`, each number is the bucket starting on
 * its window's first day instead of an ungrouped query's one row.
 */
function figureAndMirror(
  comparison: DataTileProps['comparison'],
  current: QueryResult | undefined,
  prior: QueryResult | undefined,
  dimension?: string,
): MetricComparison | undefined {
  const measureId = comparison?.current.measures[0];
  if (!comparison || !measureId || !current || !prior) return undefined;
  // No row is no number — an empty window, or a month the GROUP BY skipped.
  const figure = (result: QueryResult, window: DateWindow) => {
    const row =
      dimension === undefined
        ? result.rows[0]
        : result.rows.find((r) => {
            const key = r.dims[dimension]?.key;
            return key != null && grainStart(key) === window.from;
          });
    return row?.values[measureId] ?? null;
  };
  return {
    value: figure(current, comparison.windows.current),
    previous: figure(prior, comparison.windows.previous),
    period: comparison.windows.currentLabel,
    against: comparison.windows.previousLabel,
    againstRange: comparison.windows.previousRange,
    toDate: comparison.windows.toDate,
  };
}

/** The display-only fields a fold writes. One list, so a new one is added once. */
type Folds = Pick<ReportSpec, 'collapsed' | 'toggledGroups'>;
const foldsOf = ({ collapsed, toggledGroups }: Folds): Folds => ({ collapsed, toggledGroups });

/**
 * The spec a tile DRAWS: the saved one, with the reader's folds over it.
 *
 * The folds are kept against what they were made on: the row axis AND the
 * saved folds. A group key is the path to the group, so once the axis changes
 * — the author re-nested the report — the old keys name other groups, or
 * none; and once the author saves a different default, that default is what
 * the report now says. Either way the reader's folds are dropped rather than
 * drawn over the wrong rows or over the author's new choice.
 */
function useTileFolds(spec: ReportSpec | null) {
  // Once per saved spec, not per render: a saved `toggledGroups` can be long.
  const rows = spec?.rows;
  const savedCollapsed = spec?.collapsed;
  const savedToggled = spec?.toggledGroups;
  const basis = useMemo(
    () => JSON.stringify([rows, savedCollapsed, savedToggled]),
    [rows, savedCollapsed, savedToggled],
  );
  const [local, setLocal] = useState<{ basis: string; folds: Folds } | null>(null);
  const folds = local?.basis === basis ? local.folds : null;

  const shown = useMemo(() => (spec && folds ? { ...spec, ...folds } : spec), [spec, folds]);

  const fold = useCallback(
    (action: FoldAction) => {
      if (!shown) return;
      setLocal({ basis, folds: foldsOf(applyFold(shown, action)) });
    },
    [shown, basis],
  );

  return { shown, fold };
}

const isEmpty = (result?: QueryResult) => result !== undefined && result.rows.length === 0;

/**
 * A metric's ƒ definitions, behind an ƒ icon in its header.
 *
 * The same text the report footer discloses — `meta` first, so a formula the
 * server changed shows without a client deploy — moved up because a metric
 * tile has no footer: a disclosure row under one number was a second band of
 * chrome in a box that should be the number and its trend.
 */
function MetricDefinitions({
  spec,
  model,
  result,
}: {
  spec: ReportSpec;
  model: ReportModel;
  result: QueryResult;
}) {
  const definition = definitionsFor(model, spec, result)[0];
  if (!definition) return null;
  const measure = findMeasure(model, definition.id);
  // The same ƒ the report builder and a report's column headers use, so
  // "how is this calculated?" is one affordance across the product — hover to
  // read, click to pin. The panel carries the whole definition, not just the
  // formula line, because a tile has no footer to hold the rest.
  return (
    <FormulaHint
      label={definition.label}
      focusable
      align="end"
      className="size-6"
      contentClassName="w-96 max-w-[90vw] p-4"
    >
      <p className="font-semibold text-sm">{definition.label}</p>
      {measure?.description ? (
        <p className="mt-1 text-muted-foreground">{measure.description}</p>
      ) : null}
      <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
        {definition.text}
      </pre>
      {definition.formula ? (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground">{definition.formula}</p>
      ) : null}
    </FormulaHint>
  );
}

/**
 * A heading or a note.
 *
 * PLAIN TEXT, rendered as typed — `whitespace-pre-wrap` keeps the author's
 * line breaks without interpreting anything. Nothing in this repo parses or
 * sanitises markdown, and adding a renderer would add an injection surface to
 * a field one person fills in and everyone they share with reads.
 */
export function TextTile({
  title,
  body,
  controls,
  editing = false,
}: {
  title?: string;
  body: string;
  controls?: ReactNode;
  /** In edit mode a blank note says how to fill it; read, it is just empty. */
  editing?: boolean;
}) {
  return (
    <WidgetCard title={title ?? ''} controls={controls} wrapTitle>
      {/* Edited through Configure, not in place: typing into the tile gave no
          sign of when the text had been taken. */}
      {editing && body.trim() === '' && !title?.trim() ? (
        // A heading-only note (a row label) is complete; only a blank one asks.
        <p className="text-muted-foreground/70 text-sm italic">
          Empty note — Configure it from the tile’s menu.
        </p>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{body}</p>
      )}
    </WidgetCard>
  );
}

/**
 * A tile that cannot be drawn.
 *
 * Two causes, and the message distinguishes them because the fixes differ. A
 * MISSING REPORT was deleted or unshared — the reader may be able to get it
 * back by asking its owner. An UNKNOWN TYPE was written by a different build,
 * and the reader should do nothing at all; it will draw itself again on the
 * next deploy.
 *
 * Either way the tile keeps its place in the grid. Removing it would shift
 * every tile below and make a dashboard visibly rearrange itself over one
 * thing that failed to load.
 */
export function TombstoneTile({
  title,
  reason,
  controls,
}: {
  title?: string;
  reason: 'missing-report' | 'unknown-type';
  controls?: ReactNode;
}) {
  return (
    <WidgetCard title={title ?? 'Unavailable'} controls={controls}>
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
        <FileQuestion className="size-5" aria-hidden="true" />
        <p className="max-w-[36ch] text-xs leading-relaxed">
          {reason === 'missing-report'
            ? 'The report behind this tile is no longer available. It may have been deleted, or stopped being shared with you.'
            : 'This tile was built by a newer version of the app and cannot be shown here yet.'}
        </p>
      </div>
    </WidgetCard>
  );
}
